<!-- Toolbar actions (Save / Cancel) are registered declaratively in the
     component via PageHeaderService.setPageActions(...). -->

<div class="container mx-auto px-4 py-6 max-w-4xl">

  @if (error) {
    <div class="alert alert-error mb-4">
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <span>{{ error }}</span>
    </div>
  }

  <form [formGroup]="productForm">
    <!-- Product Mode Toggle -->
    <div class="form-control mb-6">
      <label class="label">
        <span class="label-text font-semibold">Product Type</span>
      </label>
      <ui-button-group [fullWidthMobile]="true">
        <ui-button
          [variant]="productMode === 'catalog' ? 'primary' : 'ghost'"
          [disabled]="selectedCatalogProduct !== null"
          (clicked)="setProductMode('catalog')">
          Catalog Product
        </ui-button>
        <ui-button
          [variant]="productMode === 'internal' ? 'primary' : 'ghost'"
          [disabled]="selectedCatalogProduct !== null"
          (clicked)="setProductMode('internal')">
          Internal Product
        </ui-button>
      </ui-button-group>
    </div>

    <!-- Name Field (Catalog mode first, Internal mode later) -->
    @if (productMode === 'catalog') {
      <div class="form-control mb-4">
        <label class="label" for="product-name">
          <span class="label-text font-semibold">Name</span>
        </label>

        @if (!selectedCatalogProduct) {
          <ui-input
            type="text"
            [value]="productForm.get('name')?.value || ''"
            (valueChange)="productForm.get('name')?.setValue($event)"
            placeholder="Start typing product name..."
            [fullWidth]="true"
            ariaLabel="Product name">
          </ui-input>
        }

        <!-- Catalog Search Results -->
        <div class="mt-3">
          <!-- Selected Product Card View -->
          @if (selectedCatalogProduct) {
            <div class="card bg-base-100 border-2 border-primary">
              <div class="card-body p-4">
                <div class="flex justify-between items-start mb-3">
                  <h6 class="card-title text-base">Selected Product</h6>
                  <ui-button variant="ghost" size="sm" (clicked)="clearSelectedCatalogProduct()">
                    <fa-icon [icon]="faClear" class="mr-1"></fa-icon>
                    Change
                  </ui-button>
                </div>
                <div class="flex items-start gap-3">
                  <!-- Product Image -->
                  <div class="w-24 h-24 shrink-0 rounded-lg border border-base-300 overflow-hidden relative">
                    @if (selectedCatalogProduct.image_thumb_url) {
                      <img
                        [src]="selectedCatalogProduct.image_thumb_url"
                        class="absolute inset-0 m-auto max-w-full max-h-full object-contain"
                        alt="{{selectedCatalogProduct.name}}"
                        useImageProxy/>
                    } @else {
                      <div class="absolute inset-0 flex items-center justify-center text-base-content/40">
                        <fa-icon [icon]="faProductPlaceholder" size="2x"></fa-icon>
                      </div>
                    }
                  </div>
                  <!-- Product Details -->
                  <div class="flex-1 min-w-0">
                    <div class="font-bold mb-1">{{ selectedCatalogProduct.name }}</div>
                    <div class="text-sm text-base-content/60 space-y-1">
                      @if (selectedCatalogProduct.brand) {
                        <div><strong>Brand:</strong> {{ selectedCatalogProduct.brand }}</div>
                      }
                      @if (selectedCatalogProduct.quantity) {
                        <div><strong>Quantity:</strong> {{ selectedCatalogProduct.quantity }}</div>
                      }
                      @if (selectedCatalogProduct.codes && selectedCatalogProduct.codes.length > 0) {
                        <div><strong>Barcode:</strong> {{ selectedCatalogProduct.codes[0].code }}</div>
                      }
                      @if (selectedCatalogProduct.price) {
                        <div><strong>Price:</strong> {{ selectedCatalogProduct.price }}</div>
                      }
                    </div>
                    <div class="mt-2">
                      <ui-badge variant="neutral">{{ selectedCatalogProduct.provider }}</ui-badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Provider filter badges -->
          @if (!selectedCatalogProduct && productForm.get('name')?.value?.length >= 3 && (showCatalogResults || catalogProducts.length > 0)) {
            <div class="flex flex-wrap gap-2 mb-3">
              @for (badge of providerBadges; track badge.value) {
                <button
                  type="button"
                  class="badge badge-lg cursor-pointer transition-all"
                  [class.badge-primary]="isProviderSelected(badge.value)"
                  [class.badge-outline]="!isProviderSelected(badge.value)"
                  (click)="toggleProvider(badge.value)">
                  {{ badge.label }}
                </button>
              }
            </div>
          }

          <!-- Catalog results list -->
          @if (!selectedCatalogProduct && showCatalogResults) {
            <div class="bg-base-200 rounded-lg p-3">
              <div class="flex justify-between items-center mb-2">
                <span class="text-sm text-base-content/60">{{ catalogResultTotal }} result(s) found</span>
                <ui-button variant="ghost" size="sm" (clicked)="dismissCatalogResults()">
                  <fa-icon [icon]="faClear" class="mr-1"></fa-icon>
                  Dismiss
                </ui-button>
              </div>
              <div class="max-h-96 overflow-y-auto space-y-2">
                @for (product of catalogProducts; track product.id) {
                  <div
                    class="bg-base-100 rounded-lg p-3 cursor-pointer hover:bg-base-300 transition-colors flex items-start gap-2"
                    (click)="onCatalogProductClick(product)">
                    <div class="w-10 h-10 shrink-0 rounded overflow-hidden relative">
                      @if (product.image_thumb_url) {
                        <img
                          [src]="product.image_thumb_url"
                          class="absolute inset-0 m-auto max-w-full max-h-full object-contain"
                          alt="{{product.name}}"
                          useImageProxy/>
                      } @else {
                        <div class="absolute inset-0 flex items-center justify-center text-base-content/40">
                          <fa-icon [icon]="faProductPlaceholder" size="sm"></fa-icon>
                        </div>
                      }
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="font-bold truncate">{{ product.name }}</div>
                      <div class="text-sm text-base-content/60 truncate">
                        {{ product.brand }}
                        @if (product.quantity) {
                          - {{ product.quantity }}
                        }
                      </div>
                    </div>
                    <div class="shrink-0 flex flex-col items-end gap-1">
                      <img
                        [src]="getProviderLogoPath(product.provider)"
                        [alt]="product.provider"
                        class="h-4 object-contain"
                      />
                      @if (product.price) {
                        <ui-badge variant="primary">{{ product.price }}</ui-badge>
                      }
                    </div>
                  </div>
                }

                <!-- Infinite scroll: sentinel is observed to auto-load the next page -->
                <div #catalogScrollAnchor aria-hidden="true"></div>
                @if (loadingMoreCatalog) {
                  <div class="py-2 flex items-center justify-center gap-2 text-sm text-base-content/60">
                    <span class="loading loading-spinner loading-sm"></span>
                    Loading more…
                  </div>
                }
              </div>
            </div>

            <div class="mt-2 text-center">
              <button type="button" class="btn btn-link btn-sm" (click)="dismissCatalogResults()">
                or enter product details manually
              </button>
            </div>
          }
        </div>
      </div>
    }

    <!-- Name Field: For internal mode -->
    @if (productMode === 'internal') {
      <div class="form-control mb-4">
        <label class="label" for="product-name-internal">
          <span class="label-text font-semibold">Name</span>
        </label>
        <ui-input
          type="text"
          [value]="productForm.get('name')?.value || ''"
          (valueChange)="productForm.get('name')?.setValue($event)"
          placeholder="Product name"
          [fullWidth]="true"
          ariaLabel="Product name">
        </ui-input>
      </div>
    }

    <!-- Barcode (EAN) Field: For catalog mode (after Name) -->
    @if (productMode === 'catalog' && (!showCatalogResults && !selectedCatalogProduct)) {
      <div class="form-control mb-4">
        <label class="label" for="product-code">
          <span class="label-text font-semibold">Barcode (EAN)</span>
        </label>
        <ui-input
          type="text"
          [value]="productForm.get('code')?.value || ''"
          (valueChange)="productForm.get('code')?.setValue($event)"
          placeholder="Barcode"
          [fullWidth]="true"
          ariaLabel="Barcode">
        </ui-input>
      </div>
    }

    <!-- Remaining fields: Collapsed when catalog results are shown or product is selected -->
    @if (productMode === 'internal' || (!showCatalogResults && !selectedCatalogProduct)) {
      <div>
        <div class="bg-base-200 rounded-xl p-4 mb-4 space-y-3">
          <span class="text-sm font-semibold">Quantity</span>

          <div class="flex gap-3">
            <div class="flex-1">
              <label class="label py-1">
                <span class="label-text text-xs text-base-content/60">Amount</span>
              </label>
              <ui-input
                type="text"
                [value]="productForm.get('quantityValue')?.value || ''"
                (valueChange)="productForm.get('quantityValue')?.setValue($event)"
                placeholder="0.0"
                [fullWidth]="true"
                ariaLabel="Quantity value">
              </ui-input>
            </div>
            <div class="w-36">
              <label class="label py-1">
                <span class="label-text text-xs text-base-content/60">Unit</span>
              </label>
              <ui-select
                [options]="quantityUnitOptions"
                [value]="productForm.get('quantityUnit')?.value || ''"
                (valueChange)="productForm.get('quantityUnit')?.setValue($event)"
                [fullWidth]="true"
                ariaLabel="Quantity unit">
              </ui-select>
            </div>
          </div>

          <div class="form-control">
            <label class="label cursor-pointer justify-start gap-3">
              <input
                type="checkbox"
                class="toggle toggle-primary toggle-sm"
                [checked]="productForm.get('quantityType')?.value === 'variable'"
                (change)="setQuantityType($any($event.target).checked ? 'variable' : 'fixed')"
              />
              <span class="label-text font-medium">Variable quantity</span>
            </label>
            @if (productForm.get('quantityType')?.value === 'variable') {
              <p class="text-xs text-base-content/60 ml-10">Each stock item has its own quantity (e.g. a 650g cut of cheese). The amount above is used as a default suggestion when adding stock.</p>
            } @else {
              <p class="text-xs text-base-content/60 ml-10">Fixed package size — every item has the same quantity.</p>
            }
          </div>
        </div>

        <!-- Brand Field: only for catalog products -->
        @if (productMode === 'catalog') {
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text font-semibold">Brands</span>
            </label>
            <app-tags-input
              formControlName="brands"
              placeholder="Type a brand and press Enter">
            </app-tags-input>
          </div>
        }

        <!-- Internal SKU Field: Last field for internal mode -->
        @if (productMode === 'internal') {
          <div class="form-control mb-4">
            <label class="label" for="product-code-internal">
              <span class="label-text font-semibold">Internal SKU</span>
            </label>
            <div class="flex gap-2">
              <ui-input
                type="text"
                [value]="productForm.get('code')?.value || ''"
                (valueChange)="productForm.get('code')?.setValue($event)"
                [disabled]="isGeneratedBarcodeReadonly"
                placeholder="Internal SKU"
                [fullWidth]="true"
                ariaLabel="Internal SKU">
              </ui-input>

              @if (!isGeneratedBarcodeReadonly) {
                <ui-button
                  variant="ghost"
                  (clicked)="generateInternalBarcode()"
                  [disabled]="isGeneratingBarcode || !householdId">
                  @if (isGeneratingBarcode) {
                    <span class="loading loading-spinner loading-sm"></span>
                  } @else {
                    <fa-icon [icon]="faGenerate"></fa-icon>
                  }
                </ui-button>
              }

              @if (isGeneratedBarcodeReadonly) {
                <ui-button variant="danger" (clicked)="clearGeneratedBarcode()">
                  <fa-icon [icon]="faClear"></fa-icon>
                </ui-button>
              }
            </div>
            @if (isGeneratedBarcodeReadonly) {
              <p class="text-sm text-base-content/60 mb-4 mt-2">
                This SKU was generated automatically and is locked. Click the clear button to edit it.
              </p>
            }
          </div>
        }
      </div>
    }

    <!-- Default expiry should stay visible even when a catalog product is selected -->

    <ui-card>
      <div class="form-control mb-4">
        <h3 class="card-title text-lg mb-2">Default Expiry</h3>
        <p class="text-sm text-base-content/60 mb-4">
          Used for expiration estimation when you don't set an expiry date on stock items.
        </p>

        <div class="flex gap-3 items-center flex-wrap mt-4">
          <div class="w-32">
            <ui-input
              type="number"
              [value]="productForm.get('defaultExpiryValue')?.value || ''"
              (valueChange)="productForm.get('defaultExpiryValue')?.setValue(parseIntValue($event))"
              placeholder="0"
              ariaLabel="Default expiry value">
            </ui-input>
          </div>

          <ui-button-group>
            <ui-button
              [variant]="productForm.get('defaultExpiryUnit')?.value === 'days' ? 'primary' : 'ghost'"
              size="sm"
              (clicked)="setDefaultExpiryUnit('days')">
              days
            </ui-button>
            <ui-button
              [variant]="productForm.get('defaultExpiryUnit')?.value === 'weeks' ? 'primary' : 'ghost'"
              size="sm"
              (clicked)="setDefaultExpiryUnit('weeks')">
              weeks
            </ui-button>
            <ui-button
              [variant]="productForm.get('defaultExpiryUnit')?.value === 'months' ? 'primary' : 'ghost'"
              size="sm"
              (clicked)="setDefaultExpiryUnit('months')">
              months
            </ui-button>
            <ui-button
              [variant]="productForm.get('defaultExpiryUnit')?.value === 'years' ? 'primary' : 'ghost'"
              size="sm"
              (clicked)="setDefaultExpiryUnit('years')">
              years
            </ui-button>
          </ui-button-group>
        </div>
      </div>
    </ui-card>

  </form>
</div>







